/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import IDataset from './DatasetTypes';
import { IVisualizationDynamicProperties, IVisualizationStaticProperties } from './VisualizationTypes';
import { Nullable } from './CommonTypes';
import { IFiltersDataLayout } from './MultiAxisDatasetTypes';

export interface IMultiLevelDatasetHandle {
    requestDatasetUpdate: Function,
    updateDynamicProperties: (dynamicProperties: IVisualizationDynamicProperties) => void
}

enum NodeType {
    GROUP = 'group',
    DETAIL = 'detail',
    ROOT = 'root'
}

export { NodeType };

export interface IMultiLevelDatasetNode {
    label: Nullable<string>,
    nodeType: NodeType,
    children: Array<IMultiLevelDatasetNode>,
    values: Array<string>
}

export interface IMultiLevelDatasetVisit<T> {
    (context: T): T
}

export interface IMultiLevelDatasetVisitNode<T> {
    (node: IMultiLevelDatasetNode, context: T, currentDepth: number, currentIndex: number): T
}

export interface IMultiLevelDatasetVisitor<T> {
    preVisit?: IMultiLevelDatasetVisit<T>,
    preVisitNode? : IMultiLevelDatasetVisitNode<T>,
    postVisitNode? : IMultiLevelDatasetVisitNode<T>,
    postVisit?: IMultiLevelDatasetVisit<T>
}

export interface IMultiLevelDatasetAggregation {
    field: string,
    aggregationType: string,
    aggregationFunction: string,
    timeBalanceFunctionName? : string
}

export interface IMultiLevelDatasetDetails {
    field: string,
    detailType: string
}

export interface IMultiLevelDatasetSummary {
    complete: boolean,
    empty: boolean,
    details: Array<string>,
    groups: Array<string>,
    aggregations: Array<IMultiLevelDatasetAggregation>,
    filters: IFiltersDataLayout,
    limit?: number
}

export interface IMultiLevelDataset extends IDataset {
    summary: IMultiLevelDatasetSummary,
    data: IMultiLevelDatasetNode
}

export interface IMultiLevelDatasetVisualization {
    dataset: IMultiLevelDataset,
    handle: IMultiLevelDatasetHandle,
    staticProperties: IVisualizationStaticProperties,
    dynamicProperties: IVisualizationDynamicProperties
}

export interface IMultiLevelDataServiceDataLayout {
    details: Array<IMultiLevelDatasetDetails>,
    groups: Array<string>,
    aggregations: Array<IMultiLevelDatasetAggregation>,
    distinctFields: boolean,
    filters: IFiltersDataLayout,
    order: any[],
    limit?: number
}
